"use client";

/**
 * Small italic `fx` badge that shows a formula popover on hover/focus.
 *
 * Shared by IntakeMetricSelect and MetricsPicker, which each carried a
 * near-identical copy before. Internal to intake; not exported from the
 * package barrels.
 */

import { type CSSProperties, useRef, useState } from "react";
import { createPortal } from "react-dom";

const FX_TIP_WIDTH = 280;
const FX_MARGIN = 10;

/** Small italic `fx` badge that shows a formula popover on hover/focus. */
export function FormulaButton({ formula }: { formula: string[] }) {
  const ref = useRef<HTMLButtonElement | null>(null);
  const [tipStyle, setTipStyle] = useState<CSSProperties | null>(null);

  function show() {
    const el = ref.current;
    if (!el) return;
    const b = el.getBoundingClientRect();
    const below = b.bottom + 6 + 120 < window.innerHeight;
    let left = b.left + b.width / 2;
    left = Math.max(
      FX_TIP_WIDTH / 2 + FX_MARGIN,
      Math.min(left, window.innerWidth - FX_TIP_WIDTH / 2 - FX_MARGIN),
    );
    setTipStyle({
      position: "fixed",
      left,
      top: below ? b.bottom + 6 : b.top - 6,
      transform: below
        ? "translateX(-50%)"
        : "translateX(-50%) translateY(-100%)",
      width: FX_TIP_WIDTH,
      zIndex: 9999,
    });
  }

  function hide() {
    setTipStyle(null);
  }

  return (
    <>
      <button
        ref={ref}
        type="button"
        className={[
          "inline-flex items-center justify-center",
          "h-[22px] px-[7px]",
          "border border-(--border-default) rounded-[5px]",
          "bg-(--bg-white)",
          "text-[11px] font-medium italic text-(--fg-muted)",
          "cursor-help select-none",
          "hover:border-(--border-strong) hover:text-(--fg-secondary)",
          "transition-colors duration-150",
          "font-[family-name:var(--font-body)]",
          "leading-none",
        ].join(" ")}
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        aria-label="Formula use in report"
      >
        fx
      </button>

      {tipStyle &&
        createPortal(
          <div
            className={[
              "px-3 py-2.5",
              "bg-(--bg-white)",
              "border border-(--border-default)",
              "rounded-[3px]",
              "shadow-[0_8px_20px_rgba(10,16,32,0.12),0_2px_6px_rgba(10,16,32,0.06)]",
              "pointer-events-none",
            ].join(" ")}
            style={tipStyle}
            role="tooltip"
          >
            <div className="text-[10px] font-semibold uppercase tracking-[.07em] text-(--fg-muted) mb-1.5">
              Formula use in report
            </div>
            <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5">
              {formula.map((token, idx) => {
                const isOp =
                  token === "×" ||
                  token === "÷" ||
                  token === "+" ||
                  token === "−" ||
                  token === "-" ||
                  token === "→";
                return isOp ? (
                  <span
                    key={idx}
                    className="text-[12px] font-medium text-(--fg-muted)"
                  >
                    {token}
                  </span>
                ) : (
                  <span
                    key={idx}
                    className="text-[11.5px] leading-[1.5] text-(--fg-secondary)"
                  >
                    {token}
                  </span>
                );
              })}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
